import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { openAppConfirm } from "../../components/AppDialogs";
import {
  CreateUsmleQuestionCall,
  DeleteUsmleQuestionCall,
  GenerateUsmleQuestionsCall,
  GetUsmleCatalogCall,
  GetUsmleQuestionsCall,
  SaveGeneratedUsmleQuestionsCall,
  UpdateUsmleQuestionCall,
} from "../../redux/api/usmlePrep.api";

const EMPTY_CHOICES = ["a", "b", "c", "d", "e"].map((key) => ({
  key,
  text: "",
  is_correct: key === "a",
}));

const FALLBACK_STEPS = [
  {
    id: "step_1",
    label: "Step 1",
    subjects: [
      { id: "anatomy", label: "Anatomy" },
      { id: "histology", label: "Histology" },
      { id: "physiology", label: "Physiology" },
      { id: "biochemistry", label: "Biochemistry" },
      { id: "pathology", label: "Pathology" },
      { id: "pharmacology", label: "Pharmacology" },
      { id: "microbiology", label: "Microbiology" },
      { id: "immunology", label: "Immunology" },
      { id: "behavioral", label: "Behavioral science" },
    ],
  },
  {
    id: "step_2_ck",
    label: "Step 2 CK",
    subjects: [
      { id: "internal_medicine", label: "Internal medicine" },
      { id: "surgery", label: "Surgery" },
      { id: "pediatrics", label: "Pediatrics" },
      { id: "obgyn", label: "Obstetrics & gynecology" },
      { id: "psychiatry", label: "Psychiatry" },
      { id: "neurology", label: "Neurology" },
      { id: "emergency", label: "Emergency medicine" },
      { id: "preventive", label: "Preventive medicine" },
    ],
  },
  {
    id: "step_3",
    label: "Step 3",
    subjects: [
      { id: "internal_medicine", label: "Internal medicine" },
      { id: "surgery", label: "Surgery" },
      { id: "pediatrics", label: "Pediatrics" },
      { id: "obgyn", label: "Obstetrics & gynecology" },
      { id: "psychiatry", label: "Psychiatry" },
      { id: "emergency", label: "Emergency medicine" },
      { id: "ambulatory", label: "Ambulatory care" },
    ],
  },
];

const emptyForm = (step = "step_1", subject = "anatomy") => ({
  id: null,
  step,
  subject,
  stem: "",
  explanation: "",
  difficulty: "medium",
  is_active: true,
  choices: EMPTY_CHOICES.map((c) => ({ ...c })),
});

export default function UsmlePrepAdminPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const teal = isDark ? "#2DD4BF" : "#0F766E";
  const [catalog, setCatalog] = useState(null);
  const [step, setStep] = useState("step_1");
  const [subject, setSubject] = useState("");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiCount, setAiCount] = useState(5);
  const [aiTopic, setAiTopic] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: isDark ? theme.palette.grey?.[800] ?? "#2d2d2d" : theme.palette.background.paper,
      color: theme.palette.text.primary,
      "& fieldset": { borderColor: theme.palette.divider },
    },
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
  };
  const selectSx = {
    ...inputSx,
    "& .MuiOutlinedInput-root": {
      ...inputSx["& .MuiOutlinedInput-root"],
      minHeight: 40,
      height: 40,
    },
    "& .MuiSelect-select": {
      minHeight: "40px !important",
      height: "40px !important",
      display: "flex !important",
      alignItems: "center",
      padding: "8px 36px 8px 12px !important",
      boxSizing: "border-box",
    },
    "& .MuiSelect-icon": {
      display: "inline-flex !important",
      right: 8,
    },
  };
  const selectMenuProps = {
    PaperProps: {
      sx: {
        maxHeight: 280,
        mt: 0.5,
        "& .MuiMenuItem-root": { minHeight: 36, py: 0.75 },
      },
    },
  };

  const steps = useMemo(() => {
    const remote = Array.isArray(catalog?.steps) ? catalog.steps : [];
    return FALLBACK_STEPS.map((local) => {
      const found = remote.find((s) => s.id === local.id);
      const remoteSubjects = Array.isArray(found?.subjects) ? found.subjects : [];
      const subjects = local.subjects.map((sub) => {
        const match = remoteSubjects.find((r) => r.id === sub.id);
        return {
          ...sub,
          label: match?.label || sub.label,
          question_count: Number(match?.question_count) || 0,
        };
      });
      return {
        ...local,
        label: found?.label || local.label,
        question_count: found?.question_count,
        subjects,
      };
    });
  }, [catalog]);
  const stepMeta = useMemo(() => steps.find((s) => s.id === step) || steps[0], [steps, step]);
  const subjects = stepMeta?.subjects || [];

  const loadCatalog = useCallback(async () => {
    const res = await GetUsmleCatalogCall();
    if (res?.status === 200) setCatalog(res.data);
  }, []);

  const loadQuestions = useCallback(async () => {
    const res = await GetUsmleQuestionsCall({
      step,
      subject: subject || undefined,
    });
    if (res?.status === 200 && Array.isArray(res.data)) setItems(res.data);
    else setItems([]);
  }, [step, subject]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  useEffect(() => {
    const first = FALLBACK_STEPS.find((s) => s.id === step)?.subjects?.[0]?.id || "anatomy";
    setForm(emptyForm(step, first));
  }, [step]);

  const onEdit = (item) => {
    setForm({
      id: item.id,
      step: item.step,
      subject: item.subject,
      stem: item.stem,
      explanation: item.explanation || "",
      difficulty: item.difficulty || "medium",
      is_active: item.is_active !== false,
      choices:
        (item.choices || []).length >= 2
          ? ["a", "b", "c", "d", "e"].map((key, i) => {
              const found = (item.choices || []).find((c) => c.key === key) || item.choices[i];
              return {
                key,
                text: found?.text || "",
                is_correct: !!found?.is_correct,
              };
            })
          : EMPTY_CHOICES.map((c) => ({ ...c })),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSave = async () => {
    setSaving(true);
    setMessage("");
    const body = {
      step: form.step,
      subject: form.subject,
      stem: form.stem,
      explanation: form.explanation,
      difficulty: form.difficulty,
      is_active: form.is_active,
      choices: form.choices,
    };
    const res = form.id
      ? await UpdateUsmleQuestionCall(form.id, body)
      : await CreateUsmleQuestionCall(body);
    setSaving(false);
    if (res?.status === 200 || res?.status === 201) {
      setMessage("Saved.");
      setForm(emptyForm(step, subject || subjects[0]?.id));
      loadQuestions();
      loadCatalog();
    } else {
      setMessage(res?.message || "Could not save.");
    }
  };

  const onDelete = async (item) => {
    const ok = await openAppConfirm({
      title: "Delete this USMLE question?",
      confirmText: "Delete",
    });
    if (!ok) return;
    const res = await DeleteUsmleQuestionCall(item.id);
    if (res?.status === 200) {
      loadQuestions();
      loadCatalog();
      if (form.id === item.id) setForm(emptyForm(step, subject || subjects[0]?.id));
    }
  };

  const generate = async () => {
    setAiBusy(true);
    setMessage("");
    const res = await GenerateUsmleQuestionsCall({
      step,
      subject: form.subject || subjects[0]?.id,
      count: aiCount,
      topic: aiTopic,
    });
    setAiBusy(false);
    if (res?.status === 200 && Array.isArray(res.data)) {
      setDrafts(res.data);
    } else {
      setMessage(res?.message || "AI generation failed.");
    }
  };

  const saveDrafts = async () => {
    const keepers = drafts.filter((d) => d.stem && d.explanation);
    if (!keepers.length) return;
    setAiBusy(true);
    const res = await SaveGeneratedUsmleQuestionsCall(keepers);
    setAiBusy(false);
    if (res?.status === 201 || res?.status === 200) {
      setAiOpen(false);
      setDrafts([]);
      setAiTopic("");
      loadQuestions();
      loadCatalog();
      setMessage(`Saved ${keepers.length} AI question${keepers.length === 1 ? "" : "s"}.`);
    } else {
      setMessage(res?.message || "Could not save generated questions.");
    }
  };

  const updateDraft = (idx, patch) => {
    setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Typography variant="h4" fontWeight={800} sx={{ color: teal }}>
          USMLE Prep
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Step 1 • Step 2 CK • Step 3 — generate with AI, edit, delete. Each student attempt is 10 questions with saved explanations.
        </Typography>

        <Tabs
          value={step}
          onChange={(_, v) => {
            setStep(v);
            setSubject("");
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 2,
            minHeight: 42,
            width: "fit-content",
            maxWidth: "100%",
            "& .MuiTabs-flexContainer": { height: "auto", minHeight: 34 },
            "& .MuiTab-root": {
              minHeight: 34,
              flex: "0 0 auto",
              maxWidth: "none",
              py: 0.75,
              px: 2,
            },
            "& .Mui-selected": { color: `${teal} !important` },
          }}
        >
          {steps.map((s) => (
            <Tab
              key={s.id}
              value={s.id}
              label={`${s.label}${s.question_count != null ? ` (${s.question_count})` : ""}`}
            />
          ))}
        </Tabs>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 240, ...selectSx }}>
            <InputLabel>Topic</InputLabel>
            <Select
              label="Topic"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="">All topics</MenuItem>
              {subjects.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.label} ({s.question_count || 0})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => setAiOpen(true)}
            startIcon={<AutoAwesomeRoundedIcon />}
          >
            Generate with AI
          </MDButton>
        </Stack>

        {message ? (
          <Typography variant="body2" sx={{ mb: 2, color: teal }}>
            {message}
          </Typography>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12} lg={5}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.paper,
              }}
            >
              <Typography variant="h6" fontWeight={800} sx={{ mb: 1.5 }}>
                {form.id ? "Edit question" : "New question"}
              </Typography>
              <Stack spacing={1.5}>
                <FormControl size="small" sx={selectSx}>
                  <InputLabel>Topic</InputLabel>
                  <Select
                    label="Topic"
                    value={subjects.some((s) => s.id === form.subject) ? form.subject : subjects[0]?.id || ""}
                    onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                    MenuProps={selectMenuProps}
                  >
                    {subjects.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={selectSx}>
                  <InputLabel>Difficulty</InputLabel>
                  <Select
                    label="Difficulty"
                    value={form.difficulty}
                    onChange={(e) => setForm((p) => ({ ...p, difficulty: e.target.value }))}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="easy">Easy</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="hard">Hard</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Stem"
                  multiline
                  minRows={4}
                  value={form.stem}
                  onChange={(e) => setForm((p) => ({ ...p, stem: e.target.value }))}
                  sx={inputSx}
                />
                {form.choices.map((choice, idx) => (
                  <Stack key={choice.key} direction="row" spacing={1} alignItems="flex-start">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!choice.is_correct}
                          onChange={() =>
                            setForm((p) => ({
                              ...p,
                              choices: p.choices.map((c, i) => ({
                                ...c,
                                is_correct: i === idx,
                              })),
                            }))
                          }
                        />
                      }
                      label={choice.key.toUpperCase()}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      value={choice.text}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          choices: p.choices.map((c, i) =>
                            i === idx ? { ...c, text: e.target.value } : c
                          ),
                        }))
                      }
                      sx={inputSx}
                    />
                  </Stack>
                ))}
                <TextField
                  label="Explanation (shown after the attempt)"
                  multiline
                  minRows={4}
                  value={form.explanation}
                  onChange={(e) => setForm((p) => ({ ...p, explanation: e.target.value }))}
                  sx={inputSx}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.is_active}
                      onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                    />
                  }
                  label="Active (included in student attempts)"
                />
                <Stack direction="row" spacing={1}>
                  <MDButton variant="gradient" color="info" onClick={onSave} disabled={saving}>
                    {saving ? "Saving…" : form.id ? "Update" : "Create"}
                  </MDButton>
                  {form.id ? (
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      onClick={() => setForm(emptyForm(step, subject || subjects[0]?.id))}
                    >
                      Cancel
                    </MDButton>
                  ) : null}
                </Stack>
              </Stack>
            </Box>
          </Grid>
          <Grid item xs={12} lg={7}>
            <Stack spacing={1.25}>
              {items.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    p: 1.75,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.background.paper,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={0.75} sx={{ mb: 0.75 }}>
                        <Chip
                          size="small"
                          label={subjects.find((s) => s.id === item.subject)?.label || item.subject}
                        />
                        <Chip size="small" label={item.difficulty} />
                        {!item.is_active ? <Chip size="small" color="warning" label="inactive" /> : null}
                      </Stack>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {item.stem}
                      </Typography>
                    </Box>
                    <Stack spacing={0.5}>
                      <MDButton size="small" color="info" onClick={() => onEdit(item)}>
                        Edit
                      </MDButton>
                      <MDButton size="small" color="error" onClick={() => onDelete(item)}>
                        Delete
                      </MDButton>
                    </Stack>
                  </Stack>
                </Box>
              ))}
              {!items.length ? (
                <Typography color="text.secondary">
                  No questions in this filter yet. Generate with AI or add one on the left.
                </Typography>
              ) : null}
            </Stack>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={aiOpen} onClose={() => !aiBusy && setAiOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Generate USMLE questions with AI</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Drafts land in {stepMeta?.label || step} / {subjects.find((s) => s.id === (form.subject || subjects[0]?.id))?.label}. Review, edit, then save.
            </Typography>
            <FormControl size="small" fullWidth sx={selectSx}>
              <InputLabel>Topic</InputLabel>
              <Select
                label="Topic"
                value={subjects.some((s) => s.id === form.subject) ? form.subject : subjects[0]?.id || ""}
                onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                MenuProps={selectMenuProps}
              >
                {subjects.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="number"
              label="How many"
              value={aiCount}
              onChange={(e) => setAiCount(Number(e.target.value))}
              inputProps={{ min: 1, max: 15 }}
              sx={inputSx}
            />
            <TextField
              label="Extra instruction (optional)"
              value={aiTopic}
              onChange={(e) => setAiTopic(e.target.value)}
              sx={inputSx}
            />
            {aiBusy && !drafts.length ? (
              <Stack alignItems="center" py={2}>
                <CircularProgress size={28} />
              </Stack>
            ) : null}
            {drafts.map((draft, idx) => (
              <Box key={draft.id || idx} sx={{ p: 1.5, border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5 }}>
                <TextField
                  fullWidth
                  multiline
                  label={`Stem ${idx + 1}`}
                  value={draft.stem}
                  onChange={(e) => updateDraft(idx, { stem: e.target.value })}
                  sx={{ mb: 1, ...inputSx }}
                />
                <TextField
                  fullWidth
                  multiline
                  label="Explanation"
                  value={draft.explanation}
                  onChange={(e) => updateDraft(idx, { explanation: e.target.value })}
                  sx={inputSx}
                />
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={() => setAiOpen(false)} disabled={aiBusy}>
            Close
          </MDButton>
          {!drafts.length ? (
            <MDButton color="info" onClick={generate} disabled={aiBusy}>
              {aiBusy ? "Generating…" : "Generate"}
            </MDButton>
          ) : (
            <MDButton color="info" onClick={saveDrafts} disabled={aiBusy}>
              {aiBusy ? "Saving…" : `Save ${drafts.length}`}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}
